import { EVENT_MESSAGE, RESERVE_LOW, HOLD_REQUIRED_TICKS, type NodeId, type ServerEvent } from "@spacecrew/shared";
import type { ShipState } from "@spacecrew/shared/schema";
import type { Room } from "@colyseus/sdk";
import { ship } from "../net/ship.svelte.js";

const strainLevel = (s: NonNullable<typeof ship.snap>): number =>
  Math.max(s.reserve <= 25 ? 0.6 : 0, s.activeEvent === "coolant" ? 0.4 : 0, s.breakers.status !== "idle" ? 0.3 : 0);
import { session } from "../net/session.svelte.js";
import { audio } from "./bus.js";

let effectsStarted = false;

/** Discrete events → one-shots + log. Continuous state → loops. Nothing here writes game state. */
export function wireAudio(room: Room<any, ShipState>): void {
  room.onMessage(EVENT_MESSAGE, (e: ServerEvent) => {
    ship.pushEvent(e);
    audio.play(e.hook, { nodeId: e.nodeId });
  });
  if (effectsStarted) return;
  effectsStarted = true;

  $effect.root(() => {
    $effect(() => {
      const snap = ship.snap;
      if (!snap) { audio.stopAll(); return; }
      const me = snap.crew[session.sessionId];
      if (me) audio.listenerNode = me.nodeId as NodeId;

      // The ship's room tone runs the whole time you are aboard — waiting in the lobby, under the
      // contract, and over the end card. The entry theme fades out as you board and this takes the
      // room: outside the hatch there is music, inside it there is only the ship.
      audio.loopSample("ship.idle", "/audio/ship-idle.ogg", { level: 0.85 + strainLevel(snap) * 0.25 });

      // Everything below is the contract itself: it stops between contracts, the bed does not.
      const running = snap.phase === "running";
      if (!running) { audio.stopLoops(); return; }

      // Reactor bed: pitch sags as the reserve falls below the contractual minimum; tripped breakers add wobble.
      const strain = Math.max(0, (RESERVE_LOW - snap.reserve) / RESERVE_LOW); // 0..1 below 25 %
      const detune = -strain * 700 + (snap.drainMultiplier > 1.05 ? -120 : 0) + (snap.activeEvent === "coolant" ? 60 : 0);
      audio.loop("reactor.hum", "reactor.hum.loop", { detune, level: 0.8 + strain * 0.4 });

      // Surge dump / coolant seal: whine climbs with progress.
      if (snap.surge.status === "engaged") audio.loop("hold.engaged", "hold.engaged.loop", { rate: 1 + (snap.surge.heldTicks / HOLD_REQUIRED_TICKS) * 2.5 });
      else if (snap.coolant.status === "engaged") audio.loop("hold.engaged", "hold.engaged.loop", { rate: 0.6 + (snap.coolant.heldTicks / 80) * 1.5 });
      else audio.stop("hold.engaged");

      // Footsteps for every crew member in transit, panned toward where they are heading.
      const moving = new Set<string>();
      for (const [id, c] of Object.entries(snap.crew)) {
        if (c.toNodeId) {
          moving.add(id);
          audio.loop(`steps:${id}`, "move.footsteps.loop", { nodeId: c.toNodeId as NodeId, level: id === session.sessionId ? 0.6 : 1 });
        }
      }
      for (const [id] of Object.entries(snap.crew)) if (!moving.has(id)) audio.stop(`steps:${id}`);
    });
  });
}
